/**
 * Guided import for a contact list: upload and map, check, confirm.
 *
 * The check in the middle is the whole point. A bounced message is not a
 * wasted send — it is a mark against the sending domain, and enough of them
 * throttle or suspend the mailbox, so every later campaign to good addresses
 * lands in spam. A bought or hand-typed list is where those bounces come from,
 * and it is far cheaper to find them here than to discover them halfway
 * through a send.
 *
 * The three steps exist so the judgement is seen before it is acted on: a
 * one-click import that silently dropped rows would be worse than no check,
 * because nobody would know what was thrown away.
 */
import { useMemo, useRef, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import api from '../../lib/api.js';
import { isOffline } from '../../lib/storage.js';
import { download, fileToBase64 } from '../../lib/format.js';
import { Spinner } from '../Primitives.jsx';
import { MKT_IMPORT_HEADERS } from '../../constants/marketing.js';
import { COUNTRIES, DEFAULT_COUNTRY, flagOf, formatPhone, digitsOnly } from '../../lib/phone.js';

const STEPS = ['Upload & map', 'Check the list', 'Confirm'];

/*
 * Which column is the one that matters depends on where the list is going.
 * An email campaign needs an address and can do without a number; a WhatsApp
 * or SMS campaign is the other way round.
 */
const FIELDS_BY = {
  email: [['email', 'Email', true], ['name', 'Name', false], ['phone', 'Phone', false]],
  phone: [['phone', 'Phone', true], ['name', 'Name', false], ['email', 'Email', false]]
};

const norm = h => String(h || '').toLowerCase().replace(/[^a-z]/g, '');

/** A name to greet somebody by, worked out from the address when none is given. */
const nameFromEmail = email => String(email || '')
  .split('@')[0]
  .replace(/[._-]+/g, ' ')
  .replace(/\d+/g, '')
  .trim()
  .replace(/\b\w/g, c => c.toUpperCase()) || 'there';

const VERDICT = {
  valid: ['Valid', 'v-ok'],
  risky: ['Risky', 'v-risk'],
  undeliverable: ['Undeliverable', 'v-bad'],
  dup: ['Duplicate', 'v-dup']
};

export default function ImportContacts({ onClose, onImported, channel = 'email' }) {
  const { mkt, importMktContacts, toast } = useApp();

  /* A WhatsApp or SMS list is judged by its numbers, not its addresses. */
  const byPhone = channel === 'whatsapp' || channel === 'sms';
  const FIELDS = FIELDS_BY[byPhone ? 'phone' : 'email'];
  const [defaultCountry, setDefaultCountry] = useState(DEFAULT_COUNTRY);

  const [step, setStep] = useState(1);
  const [grid, setGrid] = useState(null);          // [[cell, …], …] including the header
  const [fileName, setFileName] = useState('');
  const [pasted, setPasted] = useState('');
  const [map, setMap] = useState({ email: -1, name: -1, phone: -1 });
  const [reading, setReading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [report, setReport] = useState(null);
  const [takeRisky, setTakeRisky] = useState(true);
  const fileRef = useRef(null);

  const head = grid && grid.length ? grid[0].map(norm) : [];
  const hasHeader = head.some(h => MKT_IMPORT_HEADERS[h]);
  const bodyRows = grid ? (hasHeader ? grid.slice(1) : grid) : [];

  /** Column choices, labelled by what the header actually says. */
  const columns = useMemo(() => (grid && grid.length
    ? grid[0].map((cell, i) => [i, String(cell || `Column ${i + 1}`).slice(0, 30)])
    : []), [grid]);

  /*
   * The server reads the file, whether it is a CSV or a workbook.
   *
   * A spreadsheet reader is a megabyte of JavaScript that every visitor would
   * download whether or not they ever import anything — and the address check
   * that follows is already a round trip, so the file travels with it and
   * comes back as rows.
   */
  const readFile = async file => {
    if (!file) return;
    if (isOffline()) { toast('Reading a file needs the server'); return; }

    setReading(true);
    let parsed = [];
    try {
      const out = await api.marketing.sheet({
        name: file.name,
        data: await fileToBase64(file)
      });
      parsed = out.rows || [];
      if (out.truncated) toast(`Only the first ${parsed.length} rows were read`);
    } catch (e) {
      toast(e.message);
      setReading(false);
      return;
    }
    setReading(false);

    if (!parsed.length) { toast('That file has no rows'); return; }

    setFileName(file.name);
    setGrid(parsed);
    setPasted('');

    const h = parsed[0].map(norm);
    const known = h.some(x => MKT_IMPORT_HEADERS[x]);
    // With no header row, the first column is whatever this list is for.
    setMap({
      email: known ? h.findIndex(x => MKT_IMPORT_HEADERS[x] === 'email') : (byPhone ? -1 : 0),
      name: known ? h.findIndex(x => MKT_IMPORT_HEADERS[x] === 'name') : -1,
      phone: known ? h.findIndex(x => MKT_IMPORT_HEADERS[x] === 'phone') : (byPhone ? 0 : -1)
    });
  };

  /** Pasted addresses (or numbers) are one column, so there is nothing to map. */
  const usePasted = () => {
    const found = byPhone
      // Numbers arrive with spaces inside them, so split on lines and commas only.
      ? String(pasted).split(/[\n,;]+/).map(x => x.trim()).filter(x => digitsOnly(x).length >= 6)
      : String(pasted).split(/[\s,;]+/).map(x => x.trim()).filter(x => x.includes('@'));

    if (!found.length) { toast(byPhone ? 'No numbers in there' : 'No addresses in there'); return; }

    setGrid([[byPhone ? 'phone' : 'email'], ...found.map(e => [e])]);
    setFileName(`${found.length} pasted`);
    setMap(byPhone ? { email: -1, name: -1, phone: 0 } : { email: 0, name: -1, phone: -1 });
  };

  /* The rows as this screen understands them, before anyone judges them. */
  const staged = useMemo(() => bodyRows.map(row => ({
    email: String((map.email >= 0 && row[map.email]) || '').trim(),
    name: String((map.name >= 0 && row[map.name]) || '').trim(),
    phone: String((map.phone >= 0 && row[map.phone]) || '').trim()
  })).filter(r => (byPhone ? r.phone : r.email)), [bodyRows, map, byPhone]);

  const check = async () => {
    if (!staged.length) {
      toast(byPhone ? 'No numbers to check — is the Phone column right?' : 'No addresses to check — is the Email column right?');
      return;
    }
    if (isOffline()) { toast('Checking needs the server'); return; }

    setChecking(true);
    try {
      const out = byPhone
        ? await api.marketing.verify({
          phones: staged.map(r => r.phone),
          defaultCountry,
          known: (mkt.contacts || []).map(c => c.phone).filter(Boolean),
          optouts: mkt.optouts || []
        })
        : await api.marketing.verify({
          emails: staged.map(r => r.email),
          known: (mkt.contacts || []).map(c => c.email).filter(Boolean)
        });
      setReport(out);
      setStep(2);
    } catch (e) {
      toast(e.message);
    }
    setChecking(false);
  };

  /* What would actually be written, given the verdicts and the risky toggle. */
  const keeping = useMemo(() => {
    if (!report) return [];
    return staged
      .map((r, i) => {
        const judged = report.rows[i] || {};
        // The tidied number - country code added where it was missing.
        return byPhone && judged.phone ? { ...r, phone: judged.phone, verdict: judged.verdict } : { ...r, verdict: judged.verdict };
      })
      .filter(r => r.verdict === 'valid' || (r.verdict === 'risky' && takeRisky));
  }, [report, staged, takeRisky, byPhone]);

  const badRows = report
    ? report.rows.map((r, i) => ({ ...r, at: i, shown: byPhone ? (staged[i] || {}).phone : r.email }))
      .filter(r => r.verdict === 'undeliverable')
    : [];

  const downloadBad = () => {
    const q = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const lines = [[byPhone ? 'Phone' : 'Email', 'Why'].map(q).join(',')]
      .concat(badRows.map(r => [r.shown, r.reason || ''].map(q).join(',')));
    download(
      new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }),
      byPhone ? 'undeliverable-numbers.csv' : 'undeliverable-addresses.csv'
    );
  };

  const confirm = () => {
    if (!keeping.length) { toast('Nothing left to import'); return; }

    const { added, updated, ids } = importMktContacts(keeping.map(r => ({
      name: r.name || (r.email ? nameFromEmail(r.email) : formatPhone(r.phone)),
      email: r.email,
      phone: r.phone,
      status: 'Subscribed',
      source: 'Import',
      notes: ''
    })));

    toast(updated
      ? `${added} added, ${updated} already on the list and updated`
      : `Imported ${added} contact${added === 1 ? '' : 's'}`);

    if (onImported) onImported(ids);
    onClose();
  };

  return (
    <div className="impbox">
      <div className="imphead">
        <ol className="impsteps">
          {STEPS.map((label, i) => {
            const n = i + 1;
            return (
              <li key={label} className={n === step ? 'on' : n < step ? 'done' : ''}>
                <span>{n < step ? <Icon name="check" /> : n}</span>
                {label}
              </li>
            );
          })}
        </ol>
        <button type="button" className="btn sm" onClick={onClose}>
          <Icon name="x" />Close
        </button>
      </div>

      {/* ------------------------------------------------ 1. upload and map */}
      {step === 1 ? (
        <div className="impbody">
          <div
            className={`impdrop${grid ? ' has' : ''}`}
            onClick={() => fileRef.current && fileRef.current.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); readFile(e.dataTransfer.files[0]); }}
          >
            {reading ? <Spinner /> : <Icon name={grid ? 'check' : 'up'} />}
            {reading
              ? <b>Reading it…</b>
              : grid
                ? <b>{fileName} · {bodyRows.length} row{bodyRows.length === 1 ? '' : 's'}</b>
                : <b>Drop a CSV or Excel file here, or click to choose one</b>}
            <small>
              {grid
                ? 'Choose a different file, or map the columns below'
                : byPhone
                  ? 'CSV, XLSX or XLS. A column of numbers is enough — name and email are optional.'
                  : 'CSV, XLSX or XLS. A column of addresses is enough — name and phone are optional.'}
            </small>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls,.xlsm,.txt,text/csv"
            hidden
            onChange={e => { readFile(e.target.files && e.target.files[0]); e.target.value = ''; }}
          />

          <div className="imppaste">
            <label>{byPhone ? 'Or paste numbers, one per line' : 'Or paste addresses'}</label>
            {byPhone ? (
              <small className="imphow">
                Country code first, then the number: <b>+971 50 695 3496</b> or <b>91 82205 23246</b>.
                Spaces and dashes are fine. A number with no code gets +{defaultCountry} put in front
                (you can change that country after the numbers are read).
              </small>
            ) : null}
            <textarea
              rows="3"
              value={pasted}
              placeholder={byPhone ? '+971 50 695 3496\n+91 82205 23246\n+966 55 123 4567' : 'one@example.ae, two@example.ae …'}
              onChange={e => setPasted(e.target.value)}
            />
            <button type="button" className="btn sm" onClick={usePasted} disabled={!pasted.trim()}>
              Use these
            </button>
          </div>

          {grid ? (
            <>
              <div className="impmap">
                {FIELDS.map(([key, label, required]) => (
                  <div className="num" key={key}>
                    <label>{label}{required ? ' *' : ''}</label>
                    <select
                      value={map[key]}
                      onChange={e => setMap(m => ({ ...m, [key]: Number(e.target.value) }))}
                    >
                      <option value={-1}>— none —</option>
                      {columns.map(([i, name]) => <option key={i} value={i}>{name}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              {byPhone ? (
                <div className="num" style={{ marginTop: 10 }}>
                  <label>Country for numbers that have none</label>
                  <select value={defaultCountry} onChange={e => setDefaultCountry(e.target.value)}>
                    {COUNTRIES.map(([c, iso, name]) => (
                      <option key={c} value={c}>{flagOf(iso)} +{c} {name}</option>
                    ))}
                  </select>
                  <small>
                    A number like 50 695 3496 gets this code put in front. One that already
                    starts with a code is left alone.
                  </small>
                </div>
              ) : null}

              <div className="impfoot">
                <span className="chint">
                  {staged.length} row{staged.length === 1 ? '' : 's'} carry {byPhone ? 'a number' : 'an address'}
                  {staged.length && !staged[0].name ? (byPhone ? ' · no names, the number will stand in' : ' · names will come from the address') : ''}
                </span>
                <button
                  type="button"
                  className="btn primary"
                  onClick={check}
                  disabled={checking || !staged.length}
                >
                  {checking
                    ? <><Spinner />Checking {staged.length}…</>
                    : <>Next: check {staged.length}</>}
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {/* ------------------------------------------------------- 2. verdicts */}
      {step === 2 && report ? (
        <div className="impbody">
          {byPhone ? (
            <p className="muted" style={{ marginTop: 0 }}>
              Every number is checked for <b>shape</b>, a <b>country code</b> (added where
              missing), <b>duplicates</b>, <b>opt-outs</b>, and whether WhatsApp has
              <b> already refused it</b>. Nobody can tell whether a number is on WhatsApp before
              sending to it &mdash; Meta does not offer that lookup &mdash; so the rest is learned
              from the first send, and remembered.
            </p>
          ) : (
            <p className="muted" style={{ marginTop: 0 }}>
              Every address is checked for <b>syntax</b>, a <b>real mail server</b>,
              {' '}<b>duplicates</b>, and <b>throwaway or shared boxes</b>. Undeliverable ones are
              never imported, so a dead address cannot cost you a bounce.
            </p>
          )}

          {report.unchecked ? (
            <p className="cstop" style={{ marginBottom: 14 }}>
              <Icon name="alert" />
              <span>
                {report.unchecked} domain{report.unchecked === 1 ? '' : 's'} could not be looked up
                just now, so those are marked risky rather than judged. They are not necessarily bad.
              </span>
            </p>
          ) : null}

          <div className="impscore">
            {['valid', 'risky', 'undeliverable', 'dup'].map(k => (
              <div className={`impcount ${VERDICT[k][1]}`} key={k}>
                <span>{report.counts[k]}</span>
                <small>{byPhone && k === 'risky' ? 'Code added' : VERDICT[k][0]}</small>
              </div>
            ))}
          </div>

          {report.counts.risky ? (
            <label className="ruleopt" style={{ marginTop: 14 }}>
              <input type="checkbox" checked={takeRisky} onChange={e => setTakeRisky(e.target.checked)} />
              <span>
                <b>
                  {byPhone
                    ? `Import the ${report.counts.risky} that had no country code, with +${defaultCountry} added`
                    : `Import the ${report.counts.risky} risky one${report.counts.risky === 1 ? '' : 's'} too`}
                </b>
                <small>
                  {byPhone
                    ? 'Right if the list is from one country. Turn this off if the missing codes could be anywhere.'
                    : 'Shared boxes and throwaway domains do deliver — they just reply less and complain more. Turn this off to keep the list clean.'}
                </small>
              </span>
            </label>
          ) : null}

          {badRows.length ? (
            <div className="impbad">
              <div className="impbadhead">
                <b><Icon name="alert" />Undeliverable — never imported ({badRows.length})</b>
                <button type="button" className="lnkbtn" onClick={downloadBad}>
                  <Icon name="down" />Download
                </button>
              </div>
              <div className="impbadlist">
                {badRows.slice(0, 12).map(r => (
                  <div key={`${r.shown}-${r.at}`}>
                    <span>{r.shown || '(blank)'}</span>
                    <em>{r.reason}</em>
                  </div>
                ))}
                {badRows.length > 12
                  ? <div className="more">and {badRows.length - 12} more — download to see them all</div>
                  : null}
              </div>
            </div>
          ) : null}

          <div className="impfoot">
            <button type="button" className="btn" onClick={() => setStep(1)}>
              Back
            </button>
            <button type="button" className="btn primary" onClick={() => setStep(3)}>
              Next: confirm
            </button>
          </div>
        </div>
      ) : null}

      {/* -------------------------------------------------------- 3. confirm */}
      {step === 3 && report ? (
        <div className="impbody">
          <div className="impdone">
            <b>{keeping.length}</b>
            <span>contact{keeping.length === 1 ? '' : 's'} will be imported</span>
            <small>
              {staged.length - keeping.length} left out · from {staged.length} with {byPhone ? 'a number' : 'an address'}
            </small>
          </div>

          <p className="muted">
            {byPhone ? 'A number' : 'An address'} already on your list is updated rather than
            added twice, so importing a corrected copy of the same file will not double your send.
          </p>

          <div className="impfoot">
            <button type="button" className="btn" onClick={() => setStep(2)}>
              Back
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={confirm}
              disabled={!keeping.length}
            >
              <Icon name="check" />Import {keeping.length}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
